import {Component} from 'panel';

import {eventWasOutside} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {emitTrackEvent} from 'common/util/dom';
import {useEventListener} from 'common/util/component-helpers';

import template from './index.jade';
import css from './index.styl?inline';
import {TrackingEvent} from './tracking';

interface GlobalActionBarState {
  shortCode: string;
  shortUrlLoading: boolean;
  shortUrlCopied: boolean;
}

const shortUrlEventPrefix = `Short URL`;

export class GlobalActionBar extends Component<GlobalActionBarState> {
  private shortUrlCopiedTimeout: number;
  private shortUrlEl: HTMLInputElement;

  public static get attrsSchema() {
    return {
      ...super.attrsSchema,
      hidden: `boolean`,
      'track-name': {
        type: `string`,
        description: `If specified, includes "tab" as a property`,
      },
      'is-support-widget-present': {
        type: `boolean`,
        description: `Make room for the support widget which also shows up on the bottom right.`,
      },
    };
  }

  get config() {
    return {
      defaultState: {
        shortCode: null,
        shortUrlLoading: false,
        shortUrlCopied: false,
      } as GlobalActionBarState,
      css,
      template,
      useShadowDom: true,
      helpers: {
        createShortUrl: async () => {
          if (!this.state.shortUrlLoading) {
            const targetUrl = window.location.toString();
            try {
              this.update({shortUrlLoading: true});
              const shortUrlFetch = await fetch(`/s/`, {
                credentials: `include`,
                method: `POST`,
                body: targetUrl,
              });
              const shortCodeResp = await shortUrlFetch.text();
              if (shortUrlFetch.status !== 200) {
                throw Error(shortCodeResp);
              }
              this.update({shortCode: shortCodeResp, shortUrlLoading: false});
              this.track(TrackingEvent.Created, shortUrlEventPrefix, {
                targetUrl,
                short_code: this.state.shortCode, // keep snake_case to match server-side events
              });
            } catch (err) {
              this.update({shortUrlLoading: false});
              this.track(TrackingEvent.CreateFailed, shortUrlEventPrefix, {targetUrl, error: err});
              throw err;
            }
          }
        },

        getShortUrl: () => {
          return `${window.location.protocol}//${window.location.hostname}/s/${this.state.shortCode}`;
        },

        handleShortUrlInputInsert: (inputEl: HTMLInputElement) => {
          this.shortUrlEl = inputEl;
          this.helpers.copyShortUrl();
        },

        copyShortUrl: () => {
          this.shortUrlEl.select();
          this.shortUrlEl.style.width = `${this.shortUrlEl.scrollWidth}px`; // ensure link doesn't require text scrolling

          if (document.execCommand(`copy`)) {
            this.track(TrackingEvent.Copied, shortUrlEventPrefix, {short_code: this.state.shortCode});
            this.update({shortUrlCopied: true});
            this.shortUrlCopiedTimeout = window.setTimeout(this.helpers.clearShortUrlCopied, 2000);
          }
        },

        clearShortUrlCopied: () => {
          this.update({shortUrlCopied: false});
          window.clearTimeout(this.shortUrlCopiedTimeout);
        },

        closeShortUrl: () => {
          this.update({shortCode: null});
          this.track(TrackingEvent.Closed, shortUrlEventPrefix, {short_code: this.state.shortCode});
        },

        handleDocumentClick: (ev) => {
          if (eventWasOutside(ev, this)) {
            this.update({shortCode: null});
          }
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();
    useEventListener(this, document, `click`, this.helpers.handleDocumentClick);
  }

  private track(event, eventPrefix, props = {}) {
    let additionalProps = {};

    if (this.attr(`track-name`)) {
      additionalProps = {tab: this.attr(`track-name`)};
    }

    const eventProperties = {...props, ...additionalProps};

    emitTrackEvent(this, {
      eventName: event,
      eventPrefix,
      props: {
        ...eventProperties,
      },
      cancelable: true,
    });
  }
}

export const GlobalActionBarEl = defineAndMakeJsxFactory(`global-action-bar`, GlobalActionBar);
